import React, { useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Select, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { CopyOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { brandSemantic } from '../config/brand';
import { getDisplayNumber } from '../utils/productNumbering';
import ServiceItemModal from '../modals/ServiceItemModal';
import LogisticsItemModal from '../modals/LogisticsItemModal';
import { LOGISTICS_MODULE_ENABLED } from '../constants/featureFlags';

const { Title } = Typography;
const { Option } = Select;

export default function ProductsManagementPage({
  products,
  setProducts,
  categories,
  setCategories,
  services = [],
  setServices,
  logisticsItems = [],
  setLogisticsItems,
  suppliers = [],
  supplierProducts = [],
  productStatusFilter,
  setProductStatusFilter,
  productNameFilter,
  setProductNameFilter,
  productCategoryFilter,
  setProductCategoryFilter,
  productTypeFilter,
  setProductTypeFilter,
  setEditingProduct,
  openProductDetails,
  showModal,
  onOpenCreateChooser,
  onCopyProduct,
  canManage = true,
}) {
  const { t } = useI18n();
  const [categoryForm] = Form.useForm();
  const [categorySearchText, setCategorySearchText] = useState('');
  const [categoryStatusFilter, setCategoryStatusFilter] = useState('all');
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const [serviceSearchText, setServiceSearchText] = useState('');
  const [serviceStatusFilter, setServiceStatusFilter] = useState('all');
  const [editingService, setEditingService] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);

  const [logisticsSearchText, setLogisticsSearchText] = useState('');
  const [logisticsStatusFilter, setLogisticsStatusFilter] = useState('all');
  const [editingLogisticsItem, setEditingLogisticsItem] = useState(null);
  const [isLogisticsModalOpen, setIsLogisticsModalOpen] = useState(false);

  useEffect(() => {
    if (!isCategoryModalOpen) return;
    categoryForm.setFieldsValue({
      name: editingCategory?.name || '',
    });
  }, [categoryForm, editingCategory, isCategoryModalOpen]);

  const getEntityStatusSelectStyle = (status) => ({
    width: 112,
    '--status-color': status === 'Inactive' ? brandSemantic.error : brandSemantic.success,
    '--status-bg': status === 'Inactive' ? brandSemantic.errorSoft : brandSemantic.successSoft,
    '--status-border': status === 'Inactive' ? brandSemantic.errorBorder : brandSemantic.successBorder,
  });

  const getCategoryName = (categoryId) => (
    categories.find((category) => category.id === categoryId)?.name || t('Uncategorized')
  );

  const getSupplierName = (supplierId) => (
    suppliers.find((supplier) => supplier.id === supplierId)?.name || '—'
  );

  const openCategoryModal = (category = null) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    categoryForm.resetFields();
  };

  const handleCategorySubmit = async () => {
    const values = await categoryForm.validateFields();
    const categoryData = {
      id: editingCategory
        ? editingCategory.id
        : (categories.length > 0 ? Math.max(...categories.map((category) => category.id)) + 1 : 1),
      name: values.name.trim(),
      status: editingCategory?.status || 'Active',
    };

    setCategories((prevCategories) => (
      editingCategory
        ? prevCategories.map((category) => (category.id === editingCategory.id ? categoryData : category))
        : [...prevCategories, categoryData]
    ));

    message.success(t(editingCategory ? 'Category updated' : 'Category created'));
    closeCategoryModal();
  };

  const handleServiceSubmit = (data) => {
    const nextId = editingService
      ? editingService.id
      : (services.length > 0 ? Math.max(...services.map((s) => s.id)) + 1 : 1);
    const serviceData = { ...data, id: nextId };
    setServices((prev) => (
      editingService
        ? prev.map((s) => (s.id === editingService.id ? serviceData : s))
        : [...prev, serviceData]
    ));
    message.success(t(editingService ? 'Service updated' : 'Service created'));
    setIsServiceModalOpen(false);
    setEditingService(null);
  };

  const handleLogisticsSubmit = (data) => {
    const nextId = editingLogisticsItem
      ? editingLogisticsItem.id
      : (logisticsItems.length > 0 ? Math.max(...logisticsItems.map((l) => l.id)) + 1 : 1);
    const logisticsData = { ...data, id: nextId };
    setLogisticsItems((prev) => (
      editingLogisticsItem
        ? prev.map((l) => (l.id === editingLogisticsItem.id ? logisticsData : l))
        : [...prev, logisticsData]
    ));
    message.success(t(editingLogisticsItem ? 'Logistics item updated' : 'Logistics item created'));
    setIsLogisticsModalOpen(false);
    setEditingLogisticsItem(null);
  };

  const productColumns = [
    {
      title: t('Supplier Item Number'),
      key: 'displayNumber',
      width: 140,
      render: (_, record) => getDisplayNumber(record) || '—',
    },
    {
      title: t('Category'),
      dataIndex: 'categoryId',
      key: 'categoryId',
      width: 150,
      render: (categoryId) => getCategoryName(categoryId),
    },
    {
      title: t('Product'),
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (name, record) => (
        <Button
          type="link"
          onClick={() => openProductDetails(record)}
          style={{ padding: 0, fontWeight: 600, height: 'auto' }}
        >
          {name}
        </Button>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'productType',
      key: 'productType',
      width: 130,
      render: (productType) => (
        <Tag color={productType === 'Custom' ? 'purple' : 'blue'}>
          {t(productType === 'Custom' ? 'Custom Product' : 'Standard Product')}
        </Tag>
      ),
    },
    {
      title: t('Supplier'),
      key: 'supplier',
      width: 180,
      render: (_, record) => getSupplierName(record.supplierId),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          className="status-tag-select"
          variant="borderless"
          style={getEntityStatusSelectStyle(status)}
          onChange={(value) => {
            setProducts((prevProducts) => prevProducts.map((product) => (
              product.id === record.id ? { ...product, status: value } : product
            )));
            message.success(t(`Product marked ${value.toLowerCase()}`));
          }}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {canManage && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                setEditingProduct(record);
                showModal('editProduct');
              }}
            >
              {t('Edit')}
            </Button>
          )}
          {canManage && (
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => onCopyProduct?.(record)}
            >
              {t('Copy Product')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const categoryColumns = [
    { title: t('Category Name'), dataIndex: 'name', key: 'name', width: 240 },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          className="status-tag-select"
          variant="borderless"
          style={getEntityStatusSelectStyle(status)}
          onChange={(value) => {
            setCategories((prevCategories) => prevCategories.map((category) => (
              category.id === record.id ? { ...category, status: value } : category
            )));
            message.success(t(`Category marked ${value.toLowerCase()}`));
          }}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 72,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          style={{ padding: 0, height: 'auto' }}
          onClick={() => openCategoryModal(record)}
        >
          {t('Edit')}
        </Button>
      ),
    },
  ];

  const serviceColumns = [
    { title: t('Code'), dataIndex: 'code', key: 'code', width: 110 },
    { title: t('Service Name'), dataIndex: 'name', key: 'name', width: 220, render: (name) => t(name) },
    {
      title: t('Supplier'),
      key: 'supplier',
      width: 180,
      render: (_, record) => getSupplierName(record.supplierId),
    },
    {
      title: t('Service Type'),
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 130,
      render: (serviceType) => (
        <Tag color={serviceType === 'Charge' ? 'gold' : 'geekblue'}>{t(serviceType)}</Tag>
      ),
    },
    { title: t('Pricing Unit'), dataIndex: 'pricingUnit', key: 'pricingUnit', width: 120, render: (unit) => t(unit) },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          className="status-tag-select"
          variant="borderless"
          style={getEntityStatusSelectStyle(status)}
          onChange={(value) => {
            setServices((prev) => prev.map((s) => (s.id === record.id ? { ...s, status: value } : s)));
            message.success(t(`Service marked ${value.toLowerCase()}`));
          }}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 72,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          style={{ padding: 0, height: 'auto' }}
          onClick={() => { setEditingService(record); setIsServiceModalOpen(true); }}
        >
          {t('Edit')}
        </Button>
      ),
    },
  ];

  const logisticsColumns = [
    { title: t('Code'), dataIndex: 'code', key: 'code', width: 110 },
    { title: t('Logistics Item Name'), dataIndex: 'name', key: 'name', width: 220, render: (name) => t(name) },
    {
      title: t('Supplier'),
      key: 'supplier',
      width: 180,
      render: (_, record) => getSupplierName(record.supplierId),
    },
    { title: t('Pricing Unit'), dataIndex: 'pricingUnit', key: 'pricingUnit', width: 120, render: (unit) => t(unit) },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          className="status-tag-select"
          variant="borderless"
          style={getEntityStatusSelectStyle(status)}
          onChange={(value) => {
            setLogisticsItems((prev) => prev.map((l) => (l.id === record.id ? { ...l, status: value } : l)));
            message.success(t(`Logistics item marked ${value.toLowerCase()}`));
          }}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 72,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          style={{ padding: 0, height: 'auto' }}
          onClick={() => { setEditingLogisticsItem(record); setIsLogisticsModalOpen(true); }}
        >
          {t('Edit')}
        </Button>
      ),
    },
  ];

  const filteredProducts = products.filter((product) => {
    if (productStatusFilter !== 'all' && product.status !== productStatusFilter) return false;
    if (productCategoryFilter !== 'all' && product.categoryId !== productCategoryFilter) return false;
    if (productTypeFilter !== 'all' && product.productType !== productTypeFilter) return false;
    if (productNameFilter.trim()) {
      const query = productNameFilter.trim().toLowerCase();
      const categoryMatch = getCategoryName(product.categoryId).toLowerCase().includes(query);
      const numberMatch = (getDisplayNumber(product) || '').toLowerCase().includes(query);
      const supplierMatch = getSupplierName(product.supplierId).toLowerCase().includes(query);
      if (
        !product.name.toLowerCase().includes(query)
        && !numberMatch
        && !categoryMatch
        && !supplierMatch
      ) {
        return false;
      }
    }
    return true;
  });


  const filteredCategories = categories.filter((category) => {
    if (categoryStatusFilter !== 'all' && category.status !== categoryStatusFilter) return false;
    if (categorySearchText.trim()) {
      return category.name.toLowerCase().includes(categorySearchText.trim().toLowerCase());
    }
    return true;
  });

  const filteredServices = services.filter((service) => {
    if (serviceStatusFilter !== 'all' && service.status !== serviceStatusFilter) return false;
    if (serviceSearchText.trim()) {
      const query = serviceSearchText.trim().toLowerCase();
      return service.name.toLowerCase().includes(query) || service.code.toLowerCase().includes(query);
    }
    return true;
  });

  const filteredLogisticsItems = logisticsItems.filter((item) => {
    if (logisticsStatusFilter !== 'all' && item.status !== logisticsStatusFilter) return false;
    if (logisticsSearchText.trim()) {
      const query = logisticsSearchText.trim().toLowerCase();
      return item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query);
    }
    return true;
  });

  const productToolbar = (
    <div className="page-toolbar" style={{ marginBottom: '16px' }}>
      <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Products')}</Title>
      <Space size={12} align="center" wrap className="page-toolbar-controls">
        <Input
          placeholder={t('Search by product number, name, or supplier')}
          value={productNameFilter}
          onChange={(event) => setProductNameFilter(event.target.value)}
          style={{ width: 280, height: 32, display: 'flex', alignItems: 'center' }}
          suffix={<SearchOutlined />}
        />
        <Select
          value={productTypeFilter}
          onChange={setProductTypeFilter}
          style={{ width: 160 }}
        >
          <Option value="all">{t('All types')}</Option>
          <Option value="Standard">{t('Standard Product')}</Option>
          <Option value="Custom">{t('Custom Product')}</Option>
        </Select>
        <Select
          value={productCategoryFilter}
          onChange={setProductCategoryFilter}
          style={{ width: 170 }}
        >
          <Option value="all">{t('All categories')}</Option>
          {categories.map((category) => (
            <Option key={category.id} value={category.id}>{category.name}</Option>
          ))}
        </Select>
        <Select
          value={productStatusFilter}
          onChange={setProductStatusFilter}
          style={{ width: 150 }}
        >
          <Option value="all">{t('All statuses')}</Option>
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
        {canManage && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ minWidth: 140 }}
            onClick={onOpenCreateChooser}
          >
            {t('Add Product')}
          </Button>
        )}
      </Space>
    </div>
  );

  const categoryToolbar = (
    <div className="page-toolbar" style={{ marginBottom: '16px' }}>
      <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Categories')}</Title>
      <Space size={12} align="center" className="page-toolbar-controls">
        <Input
          placeholder={t('Search by category name')}
          value={categorySearchText}
          onChange={(event) => setCategorySearchText(event.target.value)}
          style={{ width: 260, height: 32, display: 'flex', alignItems: 'center' }}
          suffix={<SearchOutlined />}
        />
        <Select
          value={categoryStatusFilter}
          onChange={setCategoryStatusFilter}
          style={{ width: 160 }}
        >
          <Option value="all">{t('All statuses')}</Option>
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          style={{ minWidth: 150 }}
          onClick={() => openCategoryModal()}
        >
          {t('Add Category')}
        </Button>
      </Space>
    </div>
  );

  const serviceToolbar = (
    <div className="page-toolbar" style={{ marginBottom: '16px' }}>
      <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Services')}</Title>
      <Space size={12} align="center" className="page-toolbar-controls">
        <Input
          placeholder={t('Search by code or service name')}
          value={serviceSearchText}
          onChange={(event) => setServiceSearchText(event.target.value)}
          style={{ width: 260, height: 32, display: 'flex', alignItems: 'center' }}
          suffix={<SearchOutlined />}
        />
        <Select value={serviceStatusFilter} onChange={setServiceStatusFilter} style={{ width: 160 }}>
          <Option value="all">{t('All statuses')}</Option>
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
        {canManage && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ minWidth: 150 }}
            onClick={() => { setEditingService(null); setIsServiceModalOpen(true); }}
          >
            {t('Add Service')}
          </Button>
        )}
      </Space>
    </div>
  );

  const logisticsToolbar = (
    <div className="page-toolbar" style={{ marginBottom: '16px' }}>
      <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Logistics')}</Title>
      <Space size={12} align="center" className="page-toolbar-controls">
        <Input
          placeholder={t('Search by code or logistics item name')}
          value={logisticsSearchText}
          onChange={(event) => setLogisticsSearchText(event.target.value)}
          style={{ width: 260, height: 32, display: 'flex', alignItems: 'center' }}
          suffix={<SearchOutlined />}
        />
        <Select value={logisticsStatusFilter} onChange={setLogisticsStatusFilter} style={{ width: 160 }}>
          <Option value="all">{t('All statuses')}</Option>
          <Option value="Active">{t('Active')}</Option>
          <Option value="Inactive">{t('Inactive')}</Option>
        </Select>
        {canManage && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ minWidth: 150 }}
            onClick={() => { setEditingLogisticsItem(null); setIsLogisticsModalOpen(true); }}
          >
            {t('Add Logistics Item')}
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <>
      <Tabs
        defaultActiveKey="products"
        items={[
          {
            key: 'products',
            label: t('Products'),
            children: (
              <>
                {productToolbar}
                <Table
                  columns={productColumns}
                  dataSource={filteredProducts}
                  rowKey="id"
                  rowClassName={(record) => (record.status === 'Inactive' ? 'inactive-table-row' : '')}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                />
              </>
            ),
          },
          {
            key: 'categories',
            label: t('Categories'),
            children: (
              <>
                {categoryToolbar}
                <Table
                  columns={categoryColumns}
                  dataSource={filteredCategories}
                  rowKey="id"
                  rowClassName={(record) => (record.status === 'Inactive' ? 'inactive-table-row' : '')}
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                />
              </>
            ),
          },
          {
            key: 'services',
            label: t('Services'),
            children: (
              <>
                {serviceToolbar}
                <Table
                  columns={serviceColumns}
                  dataSource={filteredServices}
                  rowKey="id"
                  rowClassName={(record) => (record.status === 'Inactive' ? 'inactive-table-row' : '')}
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                />
              </>
            ),
          },
          // Logistics tab soft-removed (LOGISTICS_MODULE_ENABLED) — data,
          // handlers and the modal below are untouched so it can come back
          // by flipping the flag.
          LOGISTICS_MODULE_ENABLED && {
            key: 'logistics',
            label: t('Logistics'),
            children: (
              <>
                {logisticsToolbar}
                <Table
                  columns={logisticsColumns}
                  dataSource={filteredLogisticsItems}
                  rowKey="id"
                  rowClassName={(record) => (record.status === 'Inactive' ? 'inactive-table-row' : '')}
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                />
              </>
            ),
          },
        ].filter(Boolean)}
      />
      <Modal
        title={editingCategory ? t('Edit Category') : t('Add Category')}
        open={isCategoryModalOpen}
        onCancel={closeCategoryModal}
        width={560}
        footer={[
          <Button key="cancel" onClick={closeCategoryModal}>{t('Close')}</Button>,
          <Button key="submit" type="primary" onClick={handleCategorySubmit}>
            {editingCategory ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            label={t('Category Name')}
            name="name"
            rules={[{ required: true, message: t('Please enter category name') }]}
          >
            <Input placeholder={t('Category name')} />
          </Form.Item>
        </Form>
      </Modal>
      <ServiceItemModal
        open={isServiceModalOpen}
        onCancel={() => { setIsServiceModalOpen(false); setEditingService(null); }}
        onSubmit={handleServiceSubmit}
        editingItem={editingService}
        suppliers={suppliers}
        supplierProducts={supplierProducts}
      />
      <LogisticsItemModal
        open={isLogisticsModalOpen}
        onCancel={() => { setIsLogisticsModalOpen(false); setEditingLogisticsItem(null); }}
        onSubmit={handleLogisticsSubmit}
        editingItem={editingLogisticsItem}
        suppliers={suppliers}
        supplierProducts={supplierProducts}
      />
    </>
  );
}
